/**
 * Subscription freezes.
 *
 * A member who travels or gets injured can pause a subscription. The frozen
 * days are added onto the end date, so they get back exactly the time they
 * lost — no more, no less.
 *
 * Allowance by term:
 *
 *   1 month   no freeze
 *   3 months  2 weeks, once
 *   6 months  3 weeks, up to twice
 *
 * `maxDays` is a TOTAL BUDGET, not a per-freeze cap. A 6-month member gets
 * 3 weeks altogether and may split it over two occasions — freeze 10 days now
 * and they have 11 left, not another full 21.
 */
import type { ISODate, Subscription } from './types';
import { daysRemaining, fromISODate, isValidISODate, shiftDays, toISODate } from './dates';
import { differenceInCalendarDays } from 'date-fns';

export interface FreezeAllowance {
  /** TOTAL frozen days allowed over the life of the subscription. */
  maxDays: number;
  /** How many separate occasions that total may be split across. */
  maxFreezes: number;
  /**
   * When true, `maxDays` is a shared budget across all freezes rather than a
   * per-freeze cap. True for every real rule here; the per-freeze branch is
   * kept because the validation supports both and the policy may change.
   */
  sharedAllowance: boolean;
}

export const FREEZE_RULES: Record<number, FreezeAllowance> = {
  1: { maxDays: 0, maxFreezes: 0, sharedAllowance: true },
  // 2 weeks in total, taken in one go.
  3: { maxDays: 14, maxFreezes: 1, sharedAllowance: true },
  // 3 weeks in TOTAL, which may be split over two occasions.
  6: { maxDays: 21, maxFreezes: 2, sharedAllowance: true },
};

const NO_FREEZE: FreezeAllowance = { maxDays: 0, maxFreezes: 0, sharedAllowance: true };

export function freezeAllowance(months: number): FreezeAllowance {
  return FREEZE_RULES[months] ?? NO_FREEZE;
}

/** A freeze record as stored. */
export interface FreezeLike {
  id: string;
  subscription_id: string;
  start_date: ISODate;
  end_date: ISODate;
  days: number;
  deleted_at: string | null;
}

/** Live freezes for a subscription, oldest first. */
export function liveFreezes(freezes: readonly FreezeLike[]): FreezeLike[] {
  return freezes
    .filter((f) => f.deleted_at === null)
    .sort((a, b) => a.start_date.localeCompare(b.start_date));
}

/** Inclusive day count between two calendar days. 1 Jan → 1 Jan is 1 day. */
export function inclusiveDays(start: ISODate, end: ISODate): number {
  return differenceInCalendarDays(fromISODate(end), fromISODate(start)) + 1;
}

/** The last day of a freeze that starts on `start` and runs for `days`. */
export function freezeEndDate(start: ISODate, days: number): ISODate {
  return shiftDays(start, Math.max(1, days) - 1);
}

export interface FreezeUsage {
  used: number;
  remainingFreezes: number;
  daysUsed: number;
  /** Longest freeze that could still be taken, given the rules. */
  daysAvailable: number;
  allowance: FreezeAllowance;
  canFreeze: boolean;
}

export function freezeUsage(
  subscription: Pick<Subscription, 'months'>,
  freezes: readonly FreezeLike[]
): FreezeUsage {
  const allowance = freezeAllowance(subscription.months);
  const live = liveFreezes(freezes);
  const daysUsed = live.reduce((sum, f) => sum + f.days, 0);
  const remainingFreezes = Math.max(0, allowance.maxFreezes - live.length);

  const daysAvailable = allowance.sharedAllowance
    ? Math.max(0, allowance.maxDays - daysUsed)
    : allowance.maxDays;

  return {
    used: live.length,
    remainingFreezes,
    daysUsed,
    daysAvailable,
    allowance,
    canFreeze: remainingFreezes > 0 && daysAvailable > 0,
  };
}

/**
 * Validate a proposed freeze. Returns field errors, empty when acceptable.
 *
 * Refuses to freeze a subscription that has already expired: there is nothing
 * left to pause, and extending a dead subscription is a renewal, not a freeze.
 */
export function validateFreeze(
  subscription: Pick<Subscription, 'months' | 'end_date' | 'cancelled'>,
  existingFreezes: readonly FreezeLike[],
  startDate: ISODate,
  days: number,
  today: ISODate
): Record<string, string> {
  const errors: Record<string, string> = {};
  const usage = freezeUsage(subscription, existingFreezes);

  if (subscription.cancelled) {
    errors.subscription = 'This subscription is cancelled';
    return errors;
  }

  if (usage.allowance.maxFreezes === 0) {
    errors.subscription = `${subscription.months}-month subscriptions cannot be frozen`;
    return errors;
  }

  if (usage.remainingFreezes === 0) {
    errors.subscription =
      usage.allowance.maxFreezes === 1
        ? 'This subscription has already been frozen once'
        : `This subscription has already been frozen ${usage.allowance.maxFreezes} times`;
    return errors;
  }

  if (daysRemaining(subscription.end_date, today) < 0) {
    errors.subscription = 'This subscription has expired — renew it instead';
    return errors;
  }

  if (!isValidISODate(startDate)) {
    errors.startDate = 'Enter a valid start date';
  } else if (startDate < today) {
    // Backdating would silently hand back days the member already trained.
    errors.startDate = 'A freeze cannot start in the past';
  } else if (startDate > subscription.end_date) {
    errors.startDate = 'That is after the subscription ends';
  }

  if (!Number.isInteger(days) || days < 1) {
    errors.days = 'Enter how many days';
  } else if (days > usage.daysAvailable) {
    errors.days = `The most you can freeze is ${usage.daysAvailable} days`;
  }

  // Overlapping freezes would double-count the extension.
  if (!errors.startDate && !errors.days) {
    const proposedEnd = freezeEndDate(startDate, days);
    for (const f of liveFreezes(existingFreezes)) {
      if (startDate <= f.end_date && proposedEnd >= f.start_date) {
        errors.startDate = 'That overlaps a freeze already on this subscription';
        break;
      }
    }
  }

  return errors;
}

/** True when `date` falls inside any live freeze. */
export function isFrozenOn(freezes: readonly FreezeLike[], date: ISODate): boolean {
  return liveFreezes(freezes).some((f) => date >= f.start_date && date <= f.end_date);
}

/** The freeze covering `date`, if any. */
export function activeFreeze(
  freezes: readonly FreezeLike[],
  date: ISODate
): FreezeLike | undefined {
  return liveFreezes(freezes).find((f) => date >= f.start_date && date <= f.end_date);
}

/** The next freeze that has not started yet. */
export function upcomingFreeze(
  freezes: readonly FreezeLike[],
  date: ISODate
): FreezeLike | undefined {
  return liveFreezes(freezes).find((f) => f.start_date > date);
}

/**
 * Extend an end date by a number of frozen days.
 *
 * Applied when the freeze is created, so `end_date` on the subscription always
 * reflects reality and every existing query — status, renewals, the dashboard —
 * keeps working without knowing freezes exist. The freeze rows are the audit
 * trail for how the date got there.
 */
export function extendEndDate(endDate: ISODate, days: number): ISODate {
  return shiftDays(endDate, days);
}

/** Reverse an extension, for when a freeze is cancelled. */
export function withdrawEndDate(endDate: ISODate, days: number): ISODate {
  return shiftDays(endDate, -days);
}

/** Human summary for the profile screen. */
export function describeAllowance(months: number): string {
  const a = freezeAllowance(months);
  if (a.maxFreezes === 0) return 'Freezing is not available on this term';

  const weeks =
    a.maxDays % 7 === 0
      ? `${a.maxDays / 7} week${a.maxDays === 7 ? '' : 's'}`
      : `${a.maxDays} days`;

  if (!a.sharedAllowance) {
    return `${weeks}, ${a.maxFreezes === 1 ? 'once' : `up to ${a.maxFreezes} times`}`;
  }
  return a.maxFreezes === 1
    ? `${weeks} in total, in one go`
    : `${weeks} in total, split over up to ${a.maxFreezes} occasions`;
}

/** `toISODate` re-exported so callers do not need two imports. */
export { toISODate };
